import React, { memo } from "react";
import {
  Clock,
  AlertCircle,
  CheckCircle,
  Eye,
  ArrowRight,
  ChevronRight,
  FileText,
} from "lucide-react";
import {
  ChecklistItem,
  ChecklistSection,
  ProductsList,
} from "./SharedComponents";
import "./ListItemComponent.scss";
import IconButton from "../IconButton/IconButton";
import { EnquiryTimelineIcon } from "../../pages/Enquiries/Enquirytimelinesidebar";

// Helper function to format date and time compactly
const formatCompactDateTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dateOnly = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const timeStr = date.toLocaleTimeString("en-IN", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  if (dateOnly.getTime() === today.getTime()) {
    return `Today ${timeStr}`;
  }

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  if (dateOnly.getTime() === yesterday.getTime()) {
    return `Yesterday ${timeStr}`;
  }

  return (
    date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    }) + ` ${timeStr}`
  );
};

const ListItemComponent = memo(
  ({
    item,
    isSelected,
    canMoveToNext,
    nextStageReason,
    onToggleDetails,
    onStageProgression,
    onMoveToImmediateNext,
    onProductEdit,
    onProductClick,
    onOpenModal,
    onQuickComplete,
    getStatusIcon,
    isChecklistComplete,
    formatDateTime,
    expandedTasks,
    onOpenBOM,
    onToggleTasks,
    loadingItems,
    customPricing = false,
    isTeamView = false, // Add this prop
    onOpenMOM, // Add MOM handler
    onAddProducts,
    getApiEndpoint,
  }) => (
    <div className="list-item compact">
      <div className="list-item-main">
        {/* Left section - Status and basic info */}
        <div className="item-status-section">
          <div className="status-indicator">{getStatusIcon(item.status)}</div>
          <div className="stage-info">
            {/* STAGE NAME WITH PRIORITY BADGE */}
            <div className="stage-name">
              {item.stageName}
              {item.priority && (
                <span
                  className={`priority-badge priority-${item.priority.toLowerCase()}`}
                >
                  {item.priority}
                </span>
              )}
            </div>
            <div className="enquiry-meta">
              {item.stageDetails.sequenceNumber && (
                <span className="sequence">
                  Step {item.stageDetails.sequenceNumber}
                </span>
              )}

              {/* Client Info Inline */}
              {(item.leadName || item.clientName) && (
                <div className="client-info-inline">
                  <span className="client-name">
                    {item.leadName || item.clientName}
                  </span>
                  <span
                    className={`client-type-badge ${
                      item.leadName ? "new-client" : "existing-client"
                    }`}
                  >
                    {item.leadName ? "New" : "Existing"}
                  </span>
                </div>
              )}

              {/* DateTime Inline */}
              <div className="datetime-inline">
                {item.startedAt && (
                  <div className="date-item started">
                    <Clock size={11} />
                    <span>{formatCompactDateTime(item.startedAt)}</span>
                  </div>
                )}
                {item.endedAt && (
                  <div className="date-item ended">
                    <CheckCircle size={11} />
                    <span>{formatCompactDateTime(item.endedAt)}</span>
                  </div>
                )}
                {item.dueAt && !item.endedAt && (
                  <div className="date-item due">
                    <AlertCircle size={11} />
                    <span>
                      Due{" "}
                      {new Date(item.dueAt).toLocaleDateString("en-IN", {
                        day: "2-digit",
                        month: "short",
                      })}
                    </span>
                  </div>
                )}
              </div>

              {/* Products Display - Show Names */}
              {item.productHistories && item.productHistories.length > 0 && (
                <div className="kanban-products-list">
                  {item.productHistories.map((product) => (
                    <div
                      key={product.id}
                      className="kanban-product-item clickable"
                      onClick={(e) => {
                        e.stopPropagation();
                        onProductClick && onProductClick(product);
                      }}
                      title="Click to view product details"
                    >
                      <FileText size={12} />
                      <span className="product-name">
                        {product.productName ||
                          product.product?.name ||
                          `Product #${product.productId}`}
                      </span>
                      <span className="product-qty">×{product.quantity}</span>
                      {product.price > 0 && (
                        <span className="product-price">
                          ₹{(product.price * product.quantity).toFixed(2)}
                        </span>
                      )}
                    </div>
                  ))}

                  {/* Summary row */}
                  {item.productHistories.length > 1 && (
                    <div className="kanban-products-summary">
                      <span>Total: {item.productHistories.length} items</span>
                      {item.productHistories.every((p) => p.price > 0) && (
                        <span className="summary-total">
                          ₹
                          {item.productHistories
                            .reduce((sum, p) => sum + p.price * p.quantity, 0)
                            .toFixed(2)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Middle section - Progress and checklist */}
        <div className="item-progress-section">
          {item.checkListHistories.length > 0 && (
            <div className="progress-info">
              <div className="progress-bar mini">
                <div
                  className="progress-fill"
                  style={{ width: `${item.progress}%` }}
                />
              </div>
              <span className="task-count">
                {item.completedTasks}/{item.totalTasks}
              </span>
            </div>
          )}
          {item.checkListHistories.length > 0 && (
            <div className="inline-checklist">
              {(expandedTasks?.has(item.id)
                ? item.checkListHistories
                : item.checkListHistories.slice(0, 2)
              ).map((task) => (
                <ChecklistItem
                  key={task.id}
                  task={task}
                  itemId={item.id}
                  onOpenModal={onOpenModal}
                  onQuickComplete={onQuickComplete}
                  isLoading={loadingItems?.has(item.id) || false}
                />
              ))}

              {item.checkListHistories.length > 2 && (
                <div
                  className="more-tasks clickable"
                  onClick={() => onToggleTasks(item.id)}
                >
                  {expandedTasks?.has(item.id)
                    ? "Show Less"
                    : `+${item.checkListHistories.length - 2} more`}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right section - Actions */}
        <div className="item-actions">
          {!item.stageDetails.finalStage && (
            <>
              <div className="same-row">
                <button
                  className={`next-stage-btn compact primary ${
                    !canMoveToNext ? "disabled" : ""
                  }`}
                  onClick={() => onMoveToImmediateNext(item)}
                  disabled={!canMoveToNext}
                  title="Move to immediate next step"
                >
                  <ArrowRight size={14} />
                  Next
                </button>
                {!item.stageDetails.finalStage && (
                  <button
                    className={`next-stage-btn compact ${
                      !canMoveToNext ? "disabled" : ""
                    }`}
                    onClick={() => onStageProgression(item)}
                    disabled={!canMoveToNext}
                    title={nextStageReason || "Jump to another stage"}
                  >
                    <ChevronRight size={14} />
                    Jump
                  </button>
                )}
              </div>
            </>
          )}
          <div className="same-row">
            {isTeamView && onOpenBOM && (
              <IconButton
                icon="academicons:open-materials"
                text="BOM"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenBOM(item);
                }}
                className="bom-action-btn"
                title="Bill of Materials"
              />
            )}
            {onOpenMOM && (
              <IconButton
                icon="mdi:clock-outline"
                text="MOM"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenMOM(item);
                }}
                className="mom-action-btn"
                title="Minutes of Meeting"
              />
            )}
            {onAddProducts && (
              <IconButton
                icon="tabler:edit"
                text="Edit"
                onClick={(e) => {
                  e.stopPropagation();
                  onAddProducts(item.id);
                }}
                className="icon-button--edit"
                title="Edit products"
              />
            )}
            <button
              className="details-toggle compact"
              onClick={() => onToggleDetails(item.id)}
              title="View Details"
            >
              <Eye size={14} />
            </button>

            <EnquiryTimelineIcon
              enquiryId={item.enquiryId}
              getApiEndpoint={getApiEndpoint}
            />
          </div>

          {!isChecklistComplete(item) && (
            <div className="incomplete-indicator">
              <AlertCircle size={14} />
              <span>Pending</span>
            </div>
          )}
        </div>
      </div>

      {/* Expandable details section */}
      {isSelected && (
        <div className="item-details compact">
          <div className="details-grid">
            <div className="stage-details">
              <h4>Stage Info</h4>
              <div className="detail-items">
                {/* PRIORITY IN DETAILS SECTION */}
                {item.priority && (
                  <span className="detail-item">
                    <strong>Priority:</strong>
                    <span
                      className={`priority-badge priority-${item.priority.toLowerCase()}`}
                    >
                      {item.priority}
                    </span>
                  </span>
                )}
                {item.leadName && (
                  <span className="detail-item">
                    <strong>Lead:</strong> {item.leadName}
                  </span>
                )}
                {item.clientName && (
                  <span className="detail-item">
                    <strong>Client:</strong> {item.clientName}
                  </span>
                )}
                {isTeamView && item.assignedTo && (
                  <span className="detail-item">
                    <strong>Assigned To:</strong> {item.assignedTo}
                  </span>
                )}
                {item.stageDetails.stageDescription && (
                  <span className="detail-item">
                    <strong>Description:</strong>{" "}
                    {item.stageDetails.stageDescription}
                  </span>
                )}
                {item.startedAt && (
                  <span className="detail-item">
                    <strong>Started:</strong> {formatDateTime(item.startedAt)}
                  </span>
                )}
                {item.endedAt && (
                  <span className="detail-item">
                    <strong>Ended:</strong> {formatDateTime(item.endedAt)}
                  </span>
                )}
                {item.dueAt && (
                  <span className="detail-item">
                    <strong>Due Date:</strong>{" "}
                    {new Date(item.dueAt).toLocaleDateString()}
                  </span>
                )}
                {item.notes && (
                  <span className="detail-item">
                    <strong>Notes:</strong> {item.notes}
                  </span>
                )}
              </div>
            </div>
            {item.checkListHistories.length > 0 && (
              <ChecklistSection
                checkListHistories={item.checkListHistories}
                onOpenModal={onOpenModal}
                isComplete={isChecklistComplete(item)}
                onQuickComplete={onQuickComplete}
                loadingItems={loadingItems}
                itemId={item.id}
              />
            )}
          </div>
          {item.productHistories && item.productHistories.length > 0 && (
            <ProductsList
              productHistories={item.productHistories}
              onEdit={onProductEdit}
              onProductClick={onProductClick}
              customPricing={customPricing}
            />
          )}
          {item.remarks && (
            <div className="item-remarks">
              <strong>Remarks:</strong> {item.remarks}
            </div>
          )}
        </div>
      )}
    </div>
  ),
);

ListItemComponent.displayName = "ListItemComponent";

export default ListItemComponent;
